import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  MapPin,
  MailOpen,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { ambassadors } from "../../services/ambassadors";
import {
  AmbassadorApplication,
  AmbassadorStaff,
  ambassadorApplicationStatuses,
  ambassadorDate,
  ambassadorError,
  ambassadorPerson,
  ambassadorWeeklyHours,
} from "../../types/ambassadors";
import ConfirmDialog, { AmbassadorConfirmation } from "./ConfirmDialog";
import "./Applications.css";

const PAGE_SIZE = 30;

export default function Applications({
  staff,
  onReviewed,
  onOpenProfile,
}: {
  staff: AmbassadorStaff[];
  onReviewed: () => Promise<void>;
  onOpenProfile: (id: string) => void;
}) {
  const [query, setQuery] = useState({
    status: "PENDING",
    search: "",
    skip: 0,
  });
  const [draft, setDraft] = useState({ status: "PENDING", search: "" });
  const [items, setItems] = useState<AmbassadorApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<AmbassadorApplication>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [decision, setDecision] = useState<"APPROVED" | "REJECTED">("APPROVED");
  const [note, setNote] = useState("");
  const [supportId, setSupportId] = useState("");
  const [confirmation, setConfirmation] =
    useState<AmbassadorConfirmation | null>(null);
  const request = useRef(0);
  const mutation = useRef(false);
  const details = useRef<HTMLDivElement>(null);
  const invalidateRequests = useCallback(() => {
    request.current++;
  }, []);

  const load = useCallback(async () => {
    const version = ++request.current;
    setLoading(true);
    try {
      const result = await ambassadors.applications({
        ...(query.status ? { status: query.status } : {}),
        ...(query.search ? { search: query.search } : {}),
        skip: query.skip,
        take: PAGE_SIZE,
      });
      if (version !== request.current) return;
      setItems(result.items);
      setTotal(result.total);
      if (query.skip && !result.items.length && result.total <= query.skip) {
        setQuery((current) => ({
          ...current,
          skip: Math.max(0, current.skip - PAGE_SIZE),
        }));
      }
    } finally {
      if (version === request.current) setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    let live = true;
    setError("");
    load().catch((e) => {
      if (live) setError(ambassadorError(e));
    });
    return () => {
      live = false;
      invalidateRequests();
    };
  }, [load, invalidateRequests]);

  const refresh = async () => {
    setError("");
    try {
      await Promise.all([load(), onReviewed()]);
    } catch (e) {
      setError(ambassadorError(e));
    }
  };

  const open = (application: AmbassadorApplication) => {
    setSelected(application);
    setDecision("APPROVED");
    setNote("");
    setSupportId("");
    setError("");
    setSuccess("");
    requestAnimationFrame(() => {
      details.current?.focus();
      details.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const review = async () => {
    if (!selected || mutation.current || selected.status !== "PENDING") return;
    mutation.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await ambassadors.reviewApplication(selected.id, {
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(decision === "APPROVED" && supportId ? { supportId } : {}),
      });
      setSelected(result);
      setSuccess(
        decision === "APPROVED"
          ? "درخواست تأیید شد و پرونده سفیر برای پیگیری مراحل همکاری ایجاد شد."
          : "نتیجه بررسی ثبت شد و برای داوطلب قابل مشاهده است.",
      );
      try {
        await Promise.all([load(), onReviewed()]);
      } catch {
        setError(
          "نتیجه بررسی ثبت شد، اما فهرست به‌روز نشد. اطلاعات را تازه‌سازی کنید؛ نیازی به ثبت دوباره نیست.",
        );
      }
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      mutation.current = false;
      setBusy(false);
    }
  };

  return (
    <section
      className="amb-applications"
      aria-labelledby="amb-applications-title"
      aria-busy={busy || loading}
    >
      <div className="amb-card amb-application-toolbar">
        <div className="amb-section-title">
          <div className="amb-heading-with-icon">
            <span className="amb-heading-icon">
              <MailOpen size={20} aria-hidden="true" />
            </span>
            <div>
              <span className="amb-eyebrow">داوطلبان ثبت‌نام‌شده در سایت</span>
              <h2 id="amb-applications-title">درخواست‌های عضویت</h2>
            </div>
          </div>
          <button
            type="button"
            className="amb-secondary"
            disabled={busy || loading}
            onClick={refresh}
          >
            <RefreshCw size={16} /> تازه‌سازی
          </button>
        </div>
        <p className="amb-muted">
          اطلاعات و انگیزه داوطلب را بررسی کنید؛ با تأیید درخواست، پرونده او در
          بخش سفیران ساخته می‌شود.
        </p>
        <form
          className="amb-grid amb-application-filters"
          onSubmit={(e) => {
            e.preventDefault();
            setSelected(undefined);
            setSuccess("");
            setQuery({ ...draft, search: draft.search.trim(), skip: 0 });
          }}
        >
          <label>
            جست‌وجوی داوطلب
            <span className="amb-input-icon">
              <Search size={17} aria-hidden="true" />
              <input
                name="applicationSearch"
                value={draft.search}
                onChange={(e) =>
                  setDraft((current) => ({
                    ...current,
                    search: e.target.value,
                  }))
                }
                placeholder="نام، شماره همراه یا شهر"
                maxLength={150}
                disabled={busy}
              />
            </span>
          </label>
          <label>
            وضعیت درخواست
            <select
              name="applicationStatus"
              value={draft.status}
              onChange={(e) =>
                setDraft((current) => ({ ...current, status: e.target.value }))
              }
              disabled={busy}
            >
              <option value="">همه درخواست‌ها</option>
              {Object.entries(ambassadorApplicationStatuses).map(
                ([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ),
              )}
            </select>
          </label>
          <button type="submit" disabled={busy || loading}>
            اعمال فیلتر
          </button>
        </form>
      </div>

      {error && (
        <div className="amb-error" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="amb-success" role="status">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}

      <div className="amb-application-layout">
        <section
          className="amb-card amb-application-list"
          aria-label="فهرست درخواست‌های عضویت"
        >
          <div className="amb-section-title">
            <h3>فهرست داوطلبان</h3>
            <span className="amb-badge">
              {total.toLocaleString("fa-IR")} درخواست
            </span>
          </div>
          {loading ? (
            <div className="amb-empty" role="status">
              در حال دریافت درخواست‌ها…
            </div>
          ) : items.length ? (
            <div className="amb-application-rows">
              {items.map((application) => (
                <button
                  type="button"
                  className="amb-application-row"
                  key={application.id}
                  disabled={busy}
                  aria-label={`بررسی درخواست ${application.fullName}`}
                  aria-pressed={selected?.id === application.id}
                  onClick={() => open(application)}
                >
                  <span className="amb-avatar" aria-hidden="true">
                    {Array.from(application.fullName.trim())[0]}
                  </span>
                  <span className="amb-application-identity">
                    <strong>{application.fullName}</strong>
                    <span dir="ltr">{application.phone}</span>
                    <small>
                      <MapPin size={12} aria-hidden="true" /> {application.city}
                    </small>
                  </span>
                  <span className="amb-application-meta">
                    <span
                      className="amb-badge"
                      data-status={application.status}
                    >
                      {ambassadorApplicationStatuses[application.status]}
                    </span>
                    <small>{ambassadorDate(application.createdAt)}</small>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="amb-empty">
              <ClipboardList size={34} aria-hidden="true" />
              <h3>
                {query.status === "PENDING" && !query.search
                  ? "درخواست بررسی‌نشده‌ای ندارید"
                  : "درخواستی با این شرایط پیدا نشد"}
              </h3>
              <p>
                درخواست‌های ارسال‌شده از فرم عضویت سایت در این بخش قرار
                می‌گیرند.
              </p>
            </div>
          )}
          {total > PAGE_SIZE && (
            <div className="amb-row amb-application-pagination">
              <button
                type="button"
                className="amb-secondary"
                disabled={busy || loading || query.skip === 0}
                onClick={() =>
                  setQuery((current) => ({
                    ...current,
                    skip: Math.max(0, current.skip - PAGE_SIZE),
                  }))
                }
              >
                قبلی
              </button>
              <span className="amb-muted">
                {(query.skip + 1).toLocaleString("fa-IR")} تا{" "}
                {Math.min(query.skip + PAGE_SIZE, total).toLocaleString(
                  "fa-IR",
                )}
              </span>
              <button
                type="button"
                className="amb-secondary"
                disabled={busy || loading || query.skip + PAGE_SIZE >= total}
                onClick={() =>
                  setQuery((current) => ({
                    ...current,
                    skip: current.skip + PAGE_SIZE,
                  }))
                }
              >
                بعدی
              </button>
            </div>
          )}
        </section>

        <div
          className="amb-card amb-application-details"
          ref={details}
          tabIndex={-1}
          aria-label="جزئیات درخواست عضویت"
        >
          {!selected ? (
            <div className="amb-empty">
              <span className="amb-detail-placeholder">
                <Sparkles size={30} aria-hidden="true" />
              </span>
              <h3>آشنایی با داوطلب، پیش از شروع همکاری</h3>
              <p>
                یک درخواست را از فهرست انتخاب کنید تا اطلاعات، تجربه و انگیزه او
                را ببینید و نتیجه بررسی را ثبت کنید.
              </p>
            </div>
          ) : (
            <>
              <div className="amb-section-title amb-application-detail-heading">
                <div className="amb-heading-with-icon">
                  <span className="amb-avatar" aria-hidden="true">
                    {Array.from(selected.fullName.trim())[0]}
                  </span>
                  <div>
                    <span className="amb-eyebrow">
                      درخواست عضویت در تیم سفیران
                    </span>
                    <h2>{selected.fullName}</h2>
                  </div>
                </div>
                <span className="amb-badge" data-status={selected.status}>
                  {ambassadorApplicationStatuses[selected.status]}
                </span>
              </div>
              <dl className="amb-summary-grid">
                <div>
                  <dt>شماره همراه</dt>
                  <dd>
                    <a href={`tel:${selected.phone}`} dir="ltr">
                      {selected.phone}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt>شهر محل سکونت</dt>
                  <dd>{selected.city}</dd>
                </div>
                <div>
                  <dt>شغل یا زمینه فعالیت</dt>
                  <dd>{selected.occupation || "ثبت نشده"}</dd>
                </div>
                <div>
                  <dt>شناسه تلگرام</dt>
                  <dd dir="auto">{selected.telegramUsername || "ثبت نشده"}</dd>
                </div>
                <div>
                  <dt>زمان همکاری</dt>
                  <dd>{ambassadorWeeklyHours[selected.weeklyHours]}</dd>
                </div>
                <div>
                  <dt>زمان ارسال</dt>
                  <dd>{ambassadorDate(selected.createdAt)}</dd>
                </div>
              </dl>
              <section className="amb-application-answer">
                <h3>انگیزه همکاری</h3>
                <p className="amb-pre">{selected.motivation}</p>
              </section>
              <section className="amb-application-answer">
                <h3>تجربه و مهارت‌های مرتبط</h3>
                <p className="amb-pre">
                  {selected.experience || "تجربه‌ای ثبت نشده است."}
                </p>
              </section>
              <p className="amb-muted amb-application-consent">
                <ShieldCheck size={16} aria-hidden="true" />
                شرایط ارسال درخواست در {ambassadorDate(selected.consentAt)}{" "}
                پذیرفته شده است.
              </p>

              {selected.status === "PENDING" ? (
                <form
                  className="amb-application-review"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (decision === "REJECTED" && note.trim().length < 5) {
                      setError(
                        "برای نپذیرفتن درخواست، دلیل روشنی با حداقل ۵ نویسه بنویسید.",
                      );
                      return;
                    }
                    setError("");
                    setConfirmation({
                      title:
                        decision === "APPROVED"
                          ? `تأیید عضویت ${selected.fullName}`
                          : `نپذیرفتن درخواست ${selected.fullName}`,
                      description:
                        decision === "APPROVED"
                          ? "پرونده جذب این داوطلب ساخته می‌شود و می‌تواند مراحل همکاری را از حساب خود پیگیری کند."
                          : "نتیجه همراه با دلیل شما به داوطلب نمایش داده می‌شود. پیش از ثبت، متن پیام را بررسی کنید.",
                      label:
                        decision === "APPROVED"
                          ? "تأیید و ایجاد پرونده"
                          : "ثبت عدم پذیرش",
                      danger: decision === "REJECTED",
                      action: review,
                    });
                  }}
                >
                  <h3>ثبت نتیجه بررسی</h3>
                  <label>
                    نتیجه درخواست
                    <select
                      value={decision}
                      onChange={(e) =>
                        setDecision(e.target.value as "APPROVED" | "REJECTED")
                      }
                      disabled={busy}
                    >
                      <option value="APPROVED">
                        تأیید و شروع فرایند همکاری
                      </option>
                      <option value="REJECTED">عدم پذیرش درخواست</option>
                    </select>
                  </label>
                  {decision === "APPROVED" && (
                    <label>
                      پشتیبان سفیر (اختیاری)
                      <select
                        value={supportId}
                        onChange={(e) => setSupportId(e.target.value)}
                        disabled={busy}
                      >
                        <option value="">بعداً تعیین می‌شود</option>
                        {staff
                          .filter((member) =>
                            member.roles.some((role) =>
                              ["SUPPORT", "SUPERVISOR"].includes(role),
                            ),
                          )
                          .map((member) => (
                            <option key={member.userId} value={member.userId}>
                              {ambassadorPerson(member.user)}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  <label>
                    {decision === "REJECTED"
                      ? "دلیل عدم پذیرش"
                      : "پیام برای داوطلب (اختیاری)"}
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      required={decision === "REJECTED"}
                      maxLength={2000}
                      disabled={busy}
                      aria-describedby="amb-review-note-help"
                      placeholder={
                        decision === "REJECTED"
                          ? "دلیل تصمیم را محترمانه و روشن بنویسید."
                          : "پیام یا راهنمای شروع همکاری…"
                      }
                    />
                  </label>
                  <p id="amb-review-note-help" className="amb-muted">
                    این پیام در حساب داوطلب نمایش داده می‌شود.
                  </p>
                  <button
                    type="submit"
                    disabled={busy || loading}
                    className={decision === "REJECTED" ? "amb-danger" : ""}
                  >
                    {busy ? "در حال ثبت…" : "بازبینی و ثبت نتیجه"}
                  </button>
                </form>
              ) : (
                <section className="amb-tip">
                  <h3>نتیجه بررسی ثبت شده است</h3>
                  <p className="amb-muted">
                    زمان بررسی:{" "}
                    {ambassadorDate(selected.reviewedAt || undefined)}
                  </p>
                  {selected.reviewNote && (
                    <p className="amb-pre">{selected.reviewNote}</p>
                  )}
                  {selected.profileId && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onOpenProfile(selected.profileId!)}
                    >
                      مشاهده پرونده سفیر <ArrowLeft size={16} />
                    </button>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        confirmation={confirmation}
        busy={busy}
        onClose={() => setConfirmation(null)}
      />
    </section>
  );
}
